(function ($) {
    const SELECTOR = '[block-name="video-faq"]';
    const MUX_SCRIPT_SRC = 'https://cdn.jsdelivr.net/npm/@mux/mux-player';
    const MUX_ACCENT = '#EEC010';

    let muxLoader = null;

    /**
     * Load Mux Player once (hosted script).
     * @see https://www.mux.com/docs/guides/mux-player-web#quick-start
     */
    function loadMuxPlayer() {
        if (window.customElements && customElements.get('mux-player')) {
            return $.Deferred().resolve().promise();
        }

        if (muxLoader) {
            return muxLoader;
        }

        const $existing = $(`script[src="${MUX_SCRIPT_SRC}"]`);

        if ($existing.length) {
            muxLoader = $.Deferred((deferred) => {
                if (window.customElements && customElements.get('mux-player')) {
                    deferred.resolve();
                    return;
                }

                $existing.one('load', deferred.resolve).one('error', deferred.reject);
            }).promise();

            return muxLoader;
        }

        // A real script tag is cached by the browser. $.getScript() cache-busts
        // every request, so the player bundle would download again on each visit.
        muxLoader = $.Deferred((deferred) => {
            const script = document.createElement('script');

            script.src = MUX_SCRIPT_SRC;
            script.async = true;
            script.onload = () => deferred.resolve();
            script.onerror = () => deferred.reject();
            document.head.appendChild(script);
        }).promise();

        return muxLoader;
    }

    class VideoFaq {
        constructor($block) {
            this.$block = $block;
            this.$navButtons = $block.find('.video-faq__nav-button');
            this.$accordionToggle = $block.find('.video-faq__accordion-toggle');
            this.$accordionLabel = $block.find('.video-faq__accordion-label');
            this.$accordionList = $block.find('.video-faq__accordion-list');
            this.$accordionOptions = $block.find('.video-faq__accordion-option');
            this.$panels = $block.find('.video-faq__panel');
            this.activeIndex = 0;

            this.bindEvents();
            this.prepareActiveVideos();
        }

        bindEvents() {
            this.$navButtons.on('click', (event) => {
                this.setActiveFaq(Number($(event.currentTarget).data('faq-index')));
            });

            this.$accordionToggle.on('click', () => {
                this.toggleAccordion();
            });

            this.$accordionOptions.on('click', (event) => {
                this.setActiveFaq(Number($(event.currentTarget).data('faq-index')));
                this.closeAccordion();
            });

            this.$block.on('click', '.video-faq__play', (event) => {
                event.preventDefault();
                this.togglePlayback($(event.currentTarget).closest('.video-faq__video'));
            });

            this.$block.on('click', '.video-faq__mute', (event) => {
                event.preventDefault();
                this.toggleMute($(event.currentTarget).closest('.video-faq__video'), $(event.currentTarget));
            });

            $(document).on('click.videoFaq', (event) => {
                if (!$(event.target).closest('.video-faq__accordion').length) {
                    this.closeAccordion();
                }
            });
        }

        setActiveFaq(index) {
            if (Number.isNaN(index) || index === this.activeIndex) {
                return;
            }

            this.pauseAllVideos();
            this.activeIndex = index;

            this.$navButtons
                .removeClass('is-active')
                .attr('aria-pressed', 'false')
                .filter(`[data-faq-index="${index}"]`)
                .addClass('is-active')
                .attr('aria-pressed', 'true');

            this.$accordionOptions
                .removeClass('is-active')
                .filter(`[data-faq-index="${index}"]`)
                .addClass('is-active');

            this.$accordionLabel.text(
                this.$accordionOptions.filter(`[data-faq-index="${index}"]`).text()
            );

            this.$panels.each((_, panel) => {
                const $panel = $(panel);
                const isActive = Number($panel.data('faq-index')) === index;

                $panel.toggleClass('is-active', isActive).prop('hidden', !isActive);
            });

            this.prepareActiveVideos();
        }

        toggleAccordion() {
            if (this.$accordionToggle.attr('aria-expanded') === 'true') {
                this.closeAccordion();
            } else {
                this.openAccordion();
            }
        }

        openAccordion() {
            this.$accordionToggle.attr('aria-expanded', 'true');
            this.$accordionList.prop('hidden', false);
        }

        closeAccordion() {
            this.$accordionToggle.attr('aria-expanded', 'false');
            this.$accordionList.prop('hidden', true);
        }

        setPlayButtonState($video, isPlaying) {
            $video.toggleClass('is-playing', isPlaying);
            $video.find('.video-faq__play').attr('aria-label', isPlaying ? 'Pause video' : 'Play video');
        }

        setProgress($video, percent) {
            const clamped = Math.max(0, Math.min(1, percent / 100));
            $video.find('.video-faq__progress').css('--video-progress', clamped);
        }

        updateProgress($video, player) {
            const duration = player.duration;

            if (!duration || !Number.isFinite(duration)) {
                return;
            }

            this.setProgress($video, (player.currentTime / duration) * 100);
        }

        getPlayer($video) {
            return $video.find('mux-player').get(0) || null;
        }

        togglePlayback($video) {
            const player = this.getPlayer($video);

            if (player && $video.hasClass('is-playing')) {
                player.pause();
                return;
            }

            this.playVideo($video);
        }

        createPlayer($video, playbackId, poster, title) {
            const $player = $('<mux-player>', {
                class: 'video-faq__player'
            });

            $player
                .attr('playback-id', playbackId)
                .attr('stream-type', 'on-demand')
                .attr('preload', 'auto')
                .attr('muted', '')
                .attr('playsinline', '')
                .attr('loop', '')
                .attr('accent-color', MUX_ACCENT);

            if (poster) {
                $player.attr('poster', poster);
            }

            if (title) {
                $player.attr('metadata-video-title', title);
            }

            $player
                .on('play', () => {
                    $video.addClass('has-player');
                    this.setPlayButtonState($video, true);
                })
                .on('pause', () => {
                    this.setPlayButtonState($video, false);
                })
                .on('ended', () => {
                    const player = $player.get(0);

                    if (player) {
                        player.currentTime = 0;
                        player.play();
                    }
                })
                .on('timeupdate', () => {
                    this.updateProgress($video, $player.get(0));
                });

            $video.append($player);

            const player = $player.get(0);

            // Attribute is set before connect; also set the property once upgraded
            // so hls.js starts buffering instead of waiting for play().
            if (player) {
                player.muted = true;
                player.preload = 'auto';
            }

            return player;
        }

        /**
         * Mount players for the visible FAQ before anyone presses play, so the
         * Mux script, manifest, and first segments are already in flight.
         */
        prepareActiveVideos() {
            const $videos = this.$panels.filter('.is-active').find('.video-faq__video');

            loadMuxPlayer()
                .done(() => {
                    $videos.each((_, video) => {
                        const $video = $(video);

                        if (this.getPlayer($video) || !$video.data('playback-id')) {
                            return;
                        }

                        this.createPlayer(
                            $video,
                            $video.data('playback-id'),
                            $video.data('poster') || '',
                            $video.data('title') || ''
                        );
                    });
                })
                .fail((error) => {
                    console.error('Mux Player failed to load', error);
                });
        }

        setMuteButtonState($button, muted) {
            $button
                .attr('data-muted', muted ? 'true' : 'false')
                .attr('aria-pressed', muted ? 'true' : 'false')
                .attr('aria-label', muted ? 'Unmute video' : 'Mute video');
        }

        playVideo($video) {
            const playbackId = $video.data('playback-id');

            if (!playbackId) {
                return;
            }

            const $mute = $video.find('.video-faq__mute');
            const poster = $video.data('poster') || '';
            const title = $video.data('title') || '';

            loadMuxPlayer()
                .done(() => {
                    let player = this.getPlayer($video);

                    if (!player) {
                        player = this.createPlayer($video, playbackId, poster, title);
                    }

                    // User-initiated play: prefer unmuted audio.
                    player.muted = false;
                    this.setMuteButtonState($mute, false);

                    const playPromise = player.play();

                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {
                            player.muted = true;
                            this.setMuteButtonState($mute, true);
                            return player.play();
                        });
                    }
                })
                .fail((error) => {
                    console.error('Mux Player failed to load', error);
                });
        }

        toggleMute($video, $button) {
            const player = this.getPlayer($video);
            const currentlyMuted = $button.attr('data-muted') !== 'false';
            const nextMuted = !currentlyMuted;

            this.setMuteButtonState($button, nextMuted);

            if (player) {
                player.muted = nextMuted;
            }
        }

        pauseAllVideos() {
            this.$block.find('.video-faq__video').each((_, video) => {
                const $video = $(video);
                const $player = $video.find('mux-player');
                const player = $player.get(0);

                if (player && typeof player.pause === 'function') {
                    player.pause();
                    player.currentTime = 0;
                }

                $player.remove();
                $video.removeClass('has-player');
                this.setPlayButtonState($video, false);
                this.setProgress($video, 0);
            });
        }
    }

    $(() => {
        const $blocks = $(SELECTOR);

        if (!$blocks.length) {
            return;
        }

        $blocks.each((_, block) => {
            new VideoFaq($(block));
        });
    });
})(jQuery);
